/* People — the contact book. STORY-015.
 *
 * Everything except the display name is optional, and that is the story rather
 * than a detail of it: in a real guest list a name is the only field most
 * records have, and a form demanding a phone number gets filled with junk
 * phone numbers.
 */
import * as z from 'zod';
/** Long enough for an honorific and a business, short enough to render in a row. */
export const PERSON_NAME_MAX = 120;
export const PERSON_EMAIL_MAX = 254;
/** E.164 allows 15 digits; the raw input people type is longer than that. */
export const PERSON_PHONE_MAX = 32;
/**
 * Long enough for what a note actually is — "no nuts, arriving late with two
 * others" — and short enough that nobody mistakes it for a document.
 *
 * Bounded here as well as everywhere else because the value is encrypted
 * before storage: the column has no constraint on it, since a check constraint
 * cannot see through ciphertext. The boundary is the only place this can be
 * asserted at all. See ADR-0017.
 */
export const PERSON_NOTES_MAX = 2000;
/**
 * A name, trimmed, and never split into parts.
 *
 * One field, because name structure is not universal — `i18n.md` forbids the
 * first/last assumption, and a real list defeats it anyway: a single given
 * name, an honorific standing in for a surname, a name qualified by a business.
 * All of them save as written.
 */
const displayName = z.string().trim().min(1, 'A name is needed.').max(PERSON_NAME_MAX);
/**
 * The raw phone input, bounded but not parsed here.
 *
 * Parsing needs a default region, which is the Organisation's and therefore
 * only known on the server — so the contract carries what somebody typed and
 * the API answers with the E.164 form it stored. A client that guessed would
 * guess differently from the server, which is worse than not guessing.
 */
const phoneInput = z.string().trim().max(PERSON_PHONE_MAX);
const email = z.string().trim().max(PERSON_EMAIL_MAX).email('That is not an email address.');
/**
 * Free text about a guest.
 *
 * It stays free text on purpose. We are the processor; specifying a structured
 * dietary field would be designing the customer's contact book for them, and
 * would not stop anybody typing the same thing into a box beside it.
 *
 * The consequence is that in practice this holds **special category data under
 * Article 9** — dietary requirements imply religion, accessibility needs imply
 * disability. It is encrypted before it is stored (ADR-0017) and it is on the
 * redaction list: never logged, never traced, never in an error, never in an
 * analytics event.
 */
const notes = z.string().trim().max(PERSON_NOTES_MAX);
export const personSchema = z.object({
    id: z.string(),
    displayName: z.string(),
    /** E.164 as stored, or null. One form, whatever was typed. */
    phone: z.string().nullable(),
    email: z.string().nullable(),
    /**
     * Whether a note exists — never the note.
     *
     * A list is read by screens that show names: the contact book, and the guest
     * picker, which asks for eight matches on every keystroke. Shipping Article 9
     * free text to a picker that renders only names would put it in a query cache
     * on every search, for no reader. `security.md` calls that a minimisation
     * failure and it is the whole argument ADR-0017 makes.
     *
     * So the note travels only when somebody opens the record that holds it. The
     * boolean is what lets a row say one exists.
     */
    hasNotes: z.boolean(),
    /**
     * The subject has been severed — STORY-019, ADR-0015.
     *
     * Every identifying field reads empty or null when this is true. The client
     * renders one fixed label from it rather than showing the blank, and never a
     * name the server made up: `i18n.md` forbids storing a rendered localized
     * string, so the label belongs in the message catalogue where it can be
     * translated.
     */
    anonymised: z.boolean(),
    createdAt: z.string(),
});
/**
 * One Person, opened — the only shape that carries the note.
 *
 * Returned by reading a single record and by the writes, which already hold
 * what they just sent. Never by the list.
 */
export const personDetailSchema = personSchema.extend({
    /** Decrypted for the reader. Never searchable, never sortable — ADR-0017. */
    notes: z.string().nullable(),
    /**
     * The stored note could not be decrypted.
     *
     * Distinct from `notes: null`, which means nobody wrote one. A note rendered
     * as blank looks exactly like a note nobody wrote, and the customer would
     * never learn something was lost — so the screen says so instead.
     */
    notesUnreadable: z.boolean(),
});
export const peopleResponseSchema = z.object({
    people: z.array(personSchema),
    /** How many match the search in total, so a client can page honestly. */
    total: z.number().int(),
    /**
     * How many have had no activity for over 24 months.
     *
     * Counted and shown, never acted on. Automatic retention is off by default
     * and is the customer's decision — they are the controller and storage
     * limitation is theirs — so what this owes is making the choice visible
     * rather than making it for them. EPIC-003 risk 3.
     */
    dormant: z.number().int(),
});
export const createPersonRequestSchema = z.object({
    displayName,
    /* `nullish`, so absent and null both mean "no phone" on a create. The
     * absent/null distinction only earns its keep on a PATCH. */
    phone: phoneInput.nullish(),
    email: email.nullish(),
    notes: notes.nullish(),
});
/**
 * Absent leaves a field alone; `null` clears it.
 *
 * The same rule as every other PATCH in this API, and the same reason: without
 * it, saving a corrected name would wipe the phone number somebody added from
 * another screen.
 */
export const updatePersonRequestSchema = z.object({
    displayName: displayName.optional(),
    phone: phoneInput.nullish(),
    email: email.nullish(),
    notes: notes.nullish(),
});
export const PEOPLE_SORTS = ['name', 'created'];
export const PEOPLE_PAGE_SIZE = 25;
export const PEOPLE_PAGE_MAX = 100;
export const listPeopleQuerySchema = z.object({
    /** Matched against the folded name — accents and case ignored on both sides. */
    search: z.string().trim().max(PERSON_NAME_MAX).optional(),
    sort: z.enum(PEOPLE_SORTS).optional(),
    limit: z.number().int().min(1).max(PEOPLE_PAGE_MAX).optional(),
    offset: z.number().int().min(0).optional(),
});
/**
 * Everything held about one Person — STORY-019.
 *
 * Flat and boring on purpose: the story asks for something machine-readable,
 * and a shape that mirrors the tables is the one that stays true as they
 * change.
 */
export const personExportSchema = z.object({
    person: z.object({
        id: z.string(),
        displayName: z.string(),
        phone: z.string().nullable(),
        email: z.string().nullable(),
        notes: z.string().nullable(),
        /**
         * The stored note could not be decrypted.
         *
         * On an export this matters more than anywhere else: the reader is
         * answering somebody's subject access request, and "we hold a note we
         * cannot read" is a true answer where silence is not.
         */
        notesUnreadable: z.boolean(),
        createdAt: z.string(),
    }),
    /**
     * The part an organiser forgets is personal data, and the part the guest
     * most often means. An association recording that a named person attended a
     * particular event on a particular date is personal data even though every
     * field on the row is a status or a number.
     */
    attendances: z.array(z.object({
        eventId: z.string(),
        eventName: z.string(),
        eventDate: z.string().nullable(),
        status: z.string(),
        paymentStatus: z.string(),
        pax: z.number().int(),
        invitedAt: z.string(),
    })),
});
/**
 * What an erasure did.
 *
 * Two outcomes rather than one, because the story asks the screen to say what
 * survived: a Person nothing depended on is gone, and one carrying Attendances
 * keeps the row with the subject severed so every headcount is unchanged.
 */
export const personErasureResponseSchema = z.object({
    outcome: z.enum(['deleted', 'anonymised']),
    /** How many Attendances survived it. Zero when the row was deleted. */
    attendancesKept: z.number().int(),
});
//# sourceMappingURL=people.js.map